import { memo, useCallback } from 'react';

import { Plus } from '@air/next-icons';
import { type Board } from '@air/types';

import { WorkspaceSidebarBoardsListItemButton } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarBoardsListItemButton';
import { WorkspaceSidebarBoardsListItemDropdown } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarBoardsListItemDropdown';
import { useBoardPermissions } from '~/hooks/useBoardPermissions';
import { useCreateSubBoard } from '~/hooks/useCreateSubBoard';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';

export type WorkspacesSidebarBoardsListItemActionsProps = {
  board: Board;
  isDropdownOpen: boolean;
  onDropdownOpenChange: (open: boolean) => void;
  onRenamingStart: () => void;
};

export const WorkspacesSidebarBoardsListItemActions = memo(
  ({ board, isDropdownOpen, onDropdownOpenChange, onRenamingStart }: WorkspacesSidebarBoardsListItemActionsProps) => {
    const { boardPermissions } = useBoardPermissions({
      boardId: board.id,
    });
    const { createSubBoard } = useCreateSubBoard();

    const canAddSubBoard = canCreateBoard(boardPermissions);

    const onCreateSubBoard = useCallback(() => {
      createSubBoard({ selectedParentBoard: board, trackLocation: 'side-nav-extension-item-button' });
    }, [board, createSubBoard]);

    return (
      <div className="relative z-50 hidden shrink-0 items-center gap-1 transition-opacity group-focus-within:flex group-hover:flex group-data-[menu-open=true]:flex">
        <WorkspaceSidebarBoardsListItemDropdown
          board={board}
          open={isDropdownOpen}
          onOpenChange={onDropdownOpenChange}
          onRenamingStart={onRenamingStart}
        />
        {canAddSubBoard && (
          <WorkspaceSidebarBoardsListItemButton
            data-testid="WORKSPACE_SIDEBAR_BOARDS_LIST_ITEM_CREATE_NEW_SUB_BOARD_BUTTON"
            onClick={onCreateSubBoard}
          >
            <Plus className="size-3" />
          </WorkspaceSidebarBoardsListItemButton>
        )}
      </div>
    );
  },
);

WorkspacesSidebarBoardsListItemActions.displayName = 'WorkspacesSidebarBoardsListItemActions';
